import React, { useEffect, useState } from 'react';
import { Tag, Tooltip } from 'antd';
import {
  AppliedCorporateAction,
  CORPORATE_ACTION_TYPE_LABELS,
  getRecentCorporateActions,
} from '../../services/corporateActionApi';
import { formatDecimal, formatInt } from '../../utils/numberFormat';

/**
 * "Corporate action applied" — shown next to a config whose numbers an ex-rights
 * adjustment rewrote.
 *
 * Read-only on purpose. The point is not to offer an action but to stop the screen from
 * implying the operator chose these numbers: after a 1:1 stock dividend a grid that was
 * anchored at 20.0 reads 10.0, and without this it looks like someone re-tuned it.
 *
 * The precedent is OCC renaming an adjusted option contract (TSLA → TSLA1) so nobody trades
 * it thinking it is the standard one. Same failure, smaller surface.
 */

function ddmm(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

interface Props {
  /** Symbols on screen. Bare tickers or `EXCHANGE:TICKER` — both work. */
  symbols: string[];
  withinDays?: number;
}

export const CorporateActionBadge: React.FC<Props> = ({ symbols, withinDays = 30 }) => {
  const [actions, setActions] = useState<AppliedCorporateAction[]>([]);
  const key = symbols.join(',');

  useEffect(() => {
    let cancelled = false;
    const bare = symbols
      .map((s) => (s.includes(':') ? s.split(':').slice(1).join(':') : s))
      .filter(Boolean);
    if (bare.length === 0) {
      setActions([]);
      return;
    }
    getRecentCorporateActions(bare, withinDays)
      .then((rows) => {
        if (!cancelled) setActions(rows);
      })
      // A badge is not worth an error state: if the lookup fails the screen simply says
      // nothing, which is what it said before this existed.
      .catch(() => {
        if (!cancelled) setActions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [key, withinDays]); // eslint-disable-line react-hooks/exhaustive-deps

  if (actions.length === 0) return null;

  const newest = actions[0];
  const others = actions.length - 1;

  return (
    <Tooltip
      title={
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
          {actions.map((a) => (
            <div key={a.id}>
              <strong>{a.symbol}</strong> {CORPORATE_ACTION_TYPE_LABELS[a.type]} · ex-date {a.exDate}
              {a.priceFactor != null && <> · price ×{formatDecimal(a.priceFactor, 4)}</>}
              {a.shareFactor != null && a.shareFactor !== 1 && (
                <> · shares ×{formatDecimal(a.shareFactor, 2)}</>
              )}
              {a.cashDividend ? <> · {formatInt(a.cashDividend)} VND/share</> : null}
            </div>
          ))}
          <div style={{ marginTop: 6, opacity: 0.75 }}>
            The numbers below were re-based by the platform — they are not the values you typed.
          </div>
        </div>
      }
    >
      <Tag color="gold" style={{ marginInlineEnd: 0, cursor: 'help' }}>
        Corporate action applied ({ddmm(newest.exDate)})
        {others > 0 ? ` +${others}` : ''}
      </Tag>
    </Tooltip>
  );
};

export default CorporateActionBadge;
